
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useHeatmapData } from "./analytics/heatmap/useHeatmapData";
import { Badge } from "@/components/ui/badge";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis,  
  Tooltip, 
  ResponsiveContainer,
  PieChart, 
  Pie, 
  Cell
} from 'recharts';
import { Laptop, Smartphone, Tablet, MousePointer, ArrowDownUp, Activity } from "lucide-react";

export const HeatmapAnalytics = () => {
  const [selectedPage, setSelectedPage] = useState<string>("/");
  const { pages, pageAnalytics, isLoading } = useHeatmapData(selectedPage);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28'];
  
  const handlePageChange = (value: string) => {
    setSelectedPage(value);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const deviceData = pageAnalytics ? [
    { name: 'Desktop', value: pageAnalytics.device_breakdown.desktop },
    { name: 'Mobile', value: pageAnalytics.device_breakdown.mobile },
    { name: 'Tablet', value: pageAnalytics.device_breakdown.tablet },
  ] : [];

  const interactionData = [
    { name: 'Clicks', value: pageAnalytics?.click_count || 0 },
    { name: 'Scroll', value: pageAnalytics?.scroll_depth_avg ? Math.round(pageAnalytics.scroll_depth_avg) : 0 },
  ];

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-4">User Interaction Analytics</h2>
        <p className="text-muted-foreground mb-4">
          Analyze how users interact with different pages on your website
        </p>
        
        <div className="mb-6">
          <label className="block text-sm font-medium mb-1">Select Page</label>
          <Select
            value={selectedPage}
            onValueChange={handlePageChange}
          >
            <SelectTrigger className="w-full max-w-md">
              <SelectValue placeholder="Select a page" />
            </SelectTrigger>
            <SelectContent>
              {pages.map((page) => (
                <SelectItem key={page} value={page}>
                  {page}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {pageAnalytics ? (
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="devices">Device Breakdown</TabsTrigger>
            <TabsTrigger value="interactions">Interaction Analysis</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-4 flex items-center space-x-4">
                <div className="p-2 bg-blue-100 rounded-full">
                  <Activity className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Interactions</p>
                  <h3 className="text-2xl font-bold">{pageAnalytics.total_interactions}</h3>
                </div>
              </Card>
              
              <Card className="p-4 flex items-center space-x-4">
                <div className="p-2 bg-green-100 rounded-full">
                  <MousePointer className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Click Events</p>
                  <h3 className="text-2xl font-bold">{pageAnalytics.click_count}</h3>
                </div>
              </Card>
              
              <Card className="p-4 flex items-center space-x-4">
                <div className="p-2 bg-amber-100 rounded-full">
                  <ArrowDownUp className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Average Scroll Depth</p>
                  <h3 className="text-2xl font-bold">{Math.round(pageAnalytics.scroll_depth_avg)}%</h3>
                </div>
              </Card>
            </div>
            
            <div className="mt-8">
              <h3 className="text-lg font-medium mb-4">Page URL</h3>
              <Badge variant="outline" className="text-sm py-1 px-3">
                {pageAnalytics.page_url}
              </Badge>
            </div>
            
            <div className="mt-6 grid md:grid-cols-2 gap-6">
              <Card className="p-4">
                <h3 className="text-lg font-medium mb-4">Device Distribution</h3>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={deviceData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {deviceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`${value} interactions`, 'Count']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Card>
              
              <Card className="p-4">
                <h3 className="text-lg font-medium mb-4">Interaction Metrics</h3>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={interactionData}>
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="devices">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <Laptop className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Desktop</p>
                      <h3 className="text-2xl font-bold">{pageAnalytics.device_breakdown.desktop}</h3>
                    </div>
                  </div>
                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">
                    {pageAnalytics.total_interactions ? 
                      Math.round(pageAnalytics.device_breakdown.desktop / pageAnalytics.total_interactions * 100) : 0}%
                  </Badge>
                </Card>
                
                <Card className="p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-2 bg-green-100 rounded-full">
                      <Smartphone className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Mobile</p>
                      <h3 className="text-2xl font-bold">{pageAnalytics.device_breakdown.mobile}</h3>
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-200">
                    {pageAnalytics.total_interactions ? 
                      Math.round(pageAnalytics.device_breakdown.mobile / pageAnalytics.total_interactions * 100) : 0}%
                  </Badge>
                </Card>
                
                <Card className="p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-2 bg-amber-100 rounded-full">
                      <Tablet className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Tablet</p>
                      <h3 className="text-2xl font-bold">{pageAnalytics.device_breakdown.tablet}</h3>
                    </div>
                  </div>
                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200">
                    {pageAnalytics.total_interactions ? 
                      Math.round(pageAnalytics.device_breakdown.tablet / pageAnalytics.total_interactions * 100) : 0}%
                  </Badge>
                </Card>
              </div>

              <Card className="p-6">
                <h3 className="text-lg font-medium mb-4">Device Distribution</h3>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={deviceData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, value, percent }) => 
                          `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                        }
                      >
                        {deviceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`${value} interactions`, 'Count']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="interactions">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="p-4">
                  <h3 className="text-lg font-medium mb-4">Click Distribution</h3>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <MousePointer className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Clicks</p>
                      <h3 className="text-2xl font-bold">{pageAnalytics.click_count}</h3>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {pageAnalytics.click_count > 100 ? 
                      'High click engagement indicates active user interaction with page elements.' : 
                      'Consider optimizing call-to-action elements to increase click engagement.'}
                  </p>
                </Card>
                
                <Card className="p-4">
                  <h3 className="text-lg font-medium mb-4">Scroll Depth</h3>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="p-2 bg-green-100 rounded-full">
                      <ArrowDownUp className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Average Scroll Depth</p>
                      <h3 className="text-2xl font-bold">{Math.round(pageAnalytics.scroll_depth_avg)}%</h3>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-green-600 h-2.5 rounded-full" 
                      style={{ width: `${Math.round(pageAnalytics.scroll_depth_avg)}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-4">
                    {pageAnalytics.scroll_depth_avg > 70 ? 
                      'Users are viewing most of your content, indicating good engagement.' : 
                      'Consider moving important content higher on the page to improve visibility.'}
                  </p>
                </Card>
              </div>
              
              <Card className="p-6">
                <h3 className="text-lg font-medium mb-4">Interaction Comparison</h3>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={interactionData}>
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar 
                        dataKey="value" 
                        fill="#8884d8"
                        radius={[4, 4, 0, 0]}
                        label={{ position: 'top' }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          No analytics data available for this page
        </div>
      )}
    </Card>
  );
};
